import React, { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/components/customer/Layout";
import { CartFooter } from "@/components/customer/CartFooter";
import { ProductDialog } from "@/components/customer/ProductDialog";
import { DeliveryModeDialog } from "@/components/customer/DeliveryModeDialog";
import { useStore } from "@/context/StoreContext";
import { CHF } from "@/lib/apiClient";
import { ShoppingBag, Plus, Pencil, UtensilsCrossed, Truck } from "lucide-react";

export default function Commander() {
  const { products, categories: catDefs, delivery, setDelivery } = useStore();
  const [showDelivery, setShowDelivery] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeCat, setActiveCat] = useState("");
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!delivery) setShowDelivery(true);
  }, [delivery]);

  const categories = useMemo(() => {
    const withProducts = new Set(products.map((p) => p.category));
    const ordered = catDefs.map((c) => c.name).filter((n) => withProducts.has(n));
    products.forEach((p) => { if (!ordered.includes(p.category)) ordered.push(p.category); });
    return ordered;
  }, [products, catDefs]);

  useEffect(() => { if (categories.length && !activeCat) setActiveCat(categories[0]); }, [categories, activeCat]);

  const grouped = useMemo(() => {
    const g = {};
    products.forEach((p) => { (g[p.category] = g[p.category] || []).push(p); });
    return g;
  }, [products]);

  const scrollTo = (cat) => {
    setActiveCat(cat);
    sectionRefs.current[cat]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white min-h-screen pb-24">
      <Header />

      {delivery && (
        <div className="bg-black text-white text-sm" data-testid="delivery-banner">
          <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
            <span className="flex items-center gap-2">
              {delivery.mode === "delivery" ? <Truck className="w-4 h-4 text-brand-amber" /> : <ShoppingBag className="w-4 h-4 text-brand-amber" />}
              {delivery.mode === "delivery" ? `Livraison · ${delivery.postal_code}` : "À emporter"} · {delivery.timeslot}
            </span>
            <button onClick={() => setShowDelivery(true)} data-testid="change-delivery-btn" className="flex items-center gap-1 text-brand-amber hover:underline">
              <Pencil className="w-3 h-3" /> Modifier
            </button>
          </div>
        </div>
      )}

      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-black" data-testid="category-bar">
        <div className="max-w-4xl mx-auto px-2 flex gap-1 overflow-x-auto no-scrollbar py-2">
          {categories.map((c) => (
            <button
              key={c} onClick={() => scrollTo(c)} data-testid={`cat-${c}`}
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium border transition-colors ${activeCat === c ? "bg-brand-red text-white border-brand-red" : "border-black/20 hover:border-black"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {categories.map((cat) => (
          <section key={cat} ref={(el) => (sectionRefs.current[cat] = el)} className="pt-8 scroll-mt-32" data-testid={`section-${cat}`}>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter mb-4">{cat}</h2>
            <div className="divide-y divide-black/10 border-t border-black/10">
              {grouped[cat].map((p) => (
                <button
                  key={p.id} onClick={() => p.available && setSelected(p)} disabled={!p.available}
                  data-testid={`product-${p.id}`}
                  className="w-full flex items-center gap-4 py-4 text-left group disabled:opacity-40"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg font-bold tracking-tight group-hover:text-brand-red transition-colors">{p.name}</span>
                      {!p.available && <span className="text-xs text-brand-red">Indisponible</span>}
                    </div>
                    {p.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{p.description}</p>}
                    <span className="inline-block mt-1 font-semibold">{CHF(p.price)}</span>
                  </div>
                  <div className="relative w-24 h-24 shrink-0 overflow-hidden border border-black">
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                    ) : null}
                    <div className="w-full h-full bg-black text-brand-amber flex-col items-center justify-center gap-1 p-1" style={{ display: p.image ? "none" : "flex" }}>
                      <UtensilsCrossed className="w-6 h-6" />
                      <span className="text-[9px] leading-tight text-center text-white/60 uppercase tracking-wider line-clamp-1">{p.category}</span>
                    </div>
                    <span className="absolute bottom-1 right-1 w-7 h-7 bg-brand-red text-white flex items-center justify-center"><Plus className="w-4 h-4" /></span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <ProductDialog product={selected} open={!!selected} onClose={() => setSelected(null)} />
      <DeliveryModeDialog open={showDelivery} onClose={() => setShowDelivery(false)} />
      <CartFooter />
    </div>
  );
}
